/**
 * GoalsTab - Training goals and targets
 *
 * Contains:
 * - Current training goal display
 * - Goal dialog trigger
 * - Goal metrics visualization
 */

import { Clear as ClearIcon, Flag as FlagIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { LikelihoodUpdateResponse, TrainingGoal, UserProfile } from '@shared/types';
import React, { useState } from 'react';
import apiClient from '../../../../services/api';
import { logger } from '../../../../utils/logger';
import GoalDialog from '../../GoalDialog';

interface GoalsTabProps {
  profile: UserProfile;
  onUpdate: (updates: Partial<UserProfile>, requiresConfirmation?: boolean) => void;
  pendingChanges: Set<string>;
}

/**
 * Format a goal metric for display
 */
const formatGoalMetric = (goal: TrainingGoal): string => {
  const metric = goal.targetMetric;
  if (metric.display) return metric.display;

  switch (metric.unit) {
    case 'sec/km': {
      const mins = Math.floor(metric.value / 60);
      const secs = Math.round(metric.value % 60);
      return secs > 0 ? `${mins}m${secs}s/km` : `${mins}m/km`;
    }
    case 'W':
      return `${metric.value}W`;
    case 'km/h':
      return `${metric.value} km/h`;
    case 'km':
      return `${metric.value} km`;
    case 'sec': {
      const hours = Math.floor(metric.value / 3600);
      const minutes = Math.floor((metric.value % 3600) / 60);
      const seconds = Math.round(metric.value % 60);
      if (hours > 0) {
        return seconds > 0 ? `${hours}h${minutes}m${seconds}s` : `${hours}h${minutes}m`;
      }
      return seconds > 0 ? `${minutes}m${seconds}s` : `${minutes}m`;
    }
    default:
      return `${metric.value} ${metric.unit}`;
  }
};

const GoalsTab: React.FC<GoalsTabProps> = ({ profile, onUpdate }) => {
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [isClearingGoal, setIsClearingGoal] = useState(false);
  const [selectedGoalIdea, setSelectedGoalIdea] = useState<string | undefined>(undefined);
  const [isUpdatingLikelihood, setIsUpdatingLikelihood] = useState(false);
  const [likelihoodExplanation, setLikelihoodExplanation] =
    useState<LikelihoodUpdateResponse | null>(null);
  const [showLikelihoodDialog, setShowLikelihoodDialog] = useState(false);

  const handleGoalSaved = (goal: TrainingGoal) => {
    onUpdate({ trainingGoal: goal }, true);
    logger.info('Training goal saved', { goal: goal.goal });
  };

  const handleClearGoal = async () => {
    setIsClearingGoal(true);
    try {
      const response = await apiClient.clearTrainingGoal();
      if (response.success) {
        // Remove trainingGoal from profile
        const { trainingGoal: _, ...profileWithoutGoal } = profile;
        onUpdate(profileWithoutGoal as Partial<UserProfile>, false);
        logger.info('Training goal cleared');
      }
    } catch (error) {
      logger.error('Failed to clear goal', error as Error);
    } finally {
      setIsClearingGoal(false);
    }
  };

  const handleUpdateLikelihood = async () => {
    setIsUpdatingLikelihood(true);
    try {
      const response = await apiClient.updateGoalLikelihood();
      if (response.success && response.data) {
        setLikelihoodExplanation(response.data);
        setShowLikelihoodDialog(true);
        // Update the profile with new confidence values
        if (profile.trainingGoal) {
          onUpdate(
            {
              trainingGoal: {
                ...profile.trainingGoal,
                confidenceScore: response.data.confidenceScore,
                confidenceLabel: response.data.confidenceLabel,
              },
            },
            false
          );
        }
        logger.info('Likelihood updated', {
          newScore: response.data.confidenceScore,
          newLabel: response.data.confidenceLabel,
        });
      }
    } catch (error) {
      logger.error('Failed to update likelihood', error as Error);
    } finally {
      setIsUpdatingLikelihood(false);
    }
  };

  const goal = profile.trainingGoal;

  return (
    <Box>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          🎯 Training Goals
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set a goal to get AI-powered training recommendations.
        </Typography>
      </Box>

      {/* Current Goal or Empty State */}
      {goal ? (
        <Card
          variant="outlined"
          sx={{
            borderColor: 'primary.main',
            borderWidth: 2,
          }}
        >
          <CardContent>
            {/* Goal Header */}
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <FlagIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  {goal.goal}
                </Typography>
              </Box>
              <Chip
                label={
                  isUpdatingLikelihood ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CircularProgress size={12} color="inherit" />
                      <span>Updating...</span>
                    </Box>
                  ) : (
                    goal.confidenceLabel
                  )
                }
                size="small"
                color={
                  goal.confidenceLabel === 'high'
                    ? 'success'
                    : goal.confidenceLabel === 'medium'
                      ? 'warning'
                      : 'error'
                }
                onClick={handleUpdateLikelihood}
                disabled={isUpdatingLikelihood}
                icon={
                  !isUpdatingLikelihood ? (
                    <RefreshIcon sx={{ fontSize: '1rem !important' }} />
                  ) : undefined
                }
                sx={{
                  cursor: isUpdatingLikelihood ? 'wait' : 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                aria-label="Update goal likelihood assessment"
              />
            </Box>

            {/* Target Metric */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'primary.main',
                borderRadius: 2,
                textAlign: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.contrastText' }}>
                {formatGoalMetric(goal)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>
                Target
              </Typography>
            </Box>

            {/* Target Date */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2" color="text.secondary">
                Target Date
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {new Date(goal.targetDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Box>

            {/* Days Remaining */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="body2" color="text.secondary">
                Days Remaining
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {Math.max(
                  0,
                  Math.ceil(
                    (new Date(goal.targetDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                  )
                )}{' '}
                days
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowGoalDialog(true)}
                sx={{ flex: 1 }}
                aria-label="Change training goal"
              >
                Change Goal
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleClearGoal}
                disabled={isClearingGoal}
                startIcon={isClearingGoal ? <CircularProgress size={14} /> : <ClearIcon />}
                aria-label="Clear training goal"
              >
                Clear
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        /* Empty State */
        <Card
          variant="outlined"
          sx={{
            borderStyle: 'dashed',
            borderColor: 'divider',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <FlagIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Goal Set
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set a training goal to get personalized recommendations from our AI coach.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setShowGoalDialog(true)}
              startIcon={<FlagIcon />}
              aria-label="Set training goal"
            >
              Set a Goal
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Goal Ideas */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Goal Ideas
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {[
            'Run a marathon under 4 hours',
            'Complete a half marathon',
            'Run 10 miles under 80 minutes',
            'Complete a 200km - 3000hm grand fondo',
            'Cycle 200km in one ride',
            'Increase FTP by 10%',
            'Build all-round fitness with bike and run',
          ].map(idea => (
            <Chip
              key={idea}
              label={idea}
              size="small"
              variant="outlined"
              onClick={() => {
                setSelectedGoalIdea(idea);
                setShowGoalDialog(true);
              }}
              aria-label={`Set goal: ${idea}`}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>

      {/* Help text */}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          💡 Tip: Be specific with your goal. Include the event, target time, and date. The AI will
          analyze your current fitness and create a realistic plan.
        </Typography>
      </Box>

      {/* Goal Dialog */}
      <GoalDialog
        open={showGoalDialog}
        onClose={() => {
          setShowGoalDialog(false);
          setSelectedGoalIdea(undefined);
        }}
        onGoalSaved={handleGoalSaved}
        currentGoal={goal}
        initialGoalText={selectedGoalIdea}
      />

      {/* Likelihood Explanation Dialog */}
      <Dialog
        open={showLikelihoodDialog}
        onClose={() => setShowLikelihoodDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagIcon color="primary" />
          Goal Likelihood Assessment
        </DialogTitle>
        <DialogContent dividers>
          {likelihoodExplanation && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Score and Label */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography variant="h6">Likelihood Score</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {likelihoodExplanation.confidenceScore}%
                  </Typography>
                  <Chip
                    label={likelihoodExplanation.confidenceLabel}
                    size="small"
                    color={
                      likelihoodExplanation.confidenceLabel === 'high'
                        ? 'success'
                        : likelihoodExplanation.confidenceLabel === 'medium'
                          ? 'warning'
                          : 'error'
                    }
                  />
                </Box>
              </Box>

              {/* Summary */}
              <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2">{likelihoodExplanation.explanation.summary}</Typography>
              </Alert>

              {/* Fitness Analysis */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Fitness Analysis
                </Typography>
                <Typography variant="body2">
                  {likelihoodExplanation.explanation.fitnessAnalysis}
                </Typography>
              </Box>

              {/* Progress Analysis */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Progress Analysis
                </Typography>
                <Typography variant="body2">
                  {likelihoodExplanation.explanation.progressAnalysis}
                </Typography>
              </Box>

              {/* Time Remaining */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Time Remaining
                </Typography>
                <Typography variant="body2">
                  {likelihoodExplanation.explanation.timeRemainingAnalysis}
                </Typography>
              </Box>

              {/* Key Factors */}
              {likelihoodExplanation.explanation.keyFactors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Key Factors
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {likelihoodExplanation.explanation.keyFactors.map((factor, index) => (
                      <Typography component="li" variant="body2" key={index}>
                        {factor}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Recommendations */}
              {likelihoodExplanation.explanation.recommendations.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Recommendations
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {likelihoodExplanation.explanation.recommendations.map((rec, index) => (
                      <Typography
                        component="li"
                        variant="body2"
                        key={index}
                        sx={{ color: 'primary.main' }}
                      >
                        {rec}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Assessment Time */}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Last assessed: {new Date(likelihoodExplanation.assessedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowLikelihoodDialog(false)}
            aria-label="Close likelihood dialog"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoalsTab;
